import { useState } from "react";
import { DataTable } from "@/components/data-tables/data-table";
import {
    createEmbeddingProviderColumns,
    EmbeddingProviderSearchableColumns,
} from "./EmbeddingProviderDataTableColumns";
import { useDataTable } from "@/hooks/useDataTable";
import { Button } from "@/components/ui/button";
import { PlusCircledIcon } from "@radix-ui/react-icons";
import {
    useDeleteEmbeddingProviderMutation,
    useSetEnabledEmbeddingProviderMutation,
} from "@/api/mutations/admin/EmbeddingProviderMutations";
import { toast } from "sonner";
import { EmbeddingProviderDialog } from "@/components/dialogs/providers/EmbeddingProviderDialog";
import { Provider } from "@shared/validators/admin/settings-validators";
import { useTranslation } from "react-i18next";

export interface EmbeddingProviderActions {
    onViewDetails?: (provider: Provider) => void;
    onEdit?: (provider: Provider) => void;
    onToggleEnabled?: (provider: Provider) => void;
}

type EmbeddingProviderDataTableProps = {
    data: Provider[];
    actions?: EmbeddingProviderActions;
};

const EmbeddingProviderDataTable = ({ data, actions }: EmbeddingProviderDataTableProps) => {
    const { t } = useTranslation();
    const [createDialogOpen, setCreateDialogOpen] = useState(false);
    const [editDialogOpen, setEditDialogOpen] = useState(false);
    const [selectedProvider, setSelectedProvider] = useState<Provider | null>(null);

    const setEnabledEmbeddingProviderMutation = useSetEnabledEmbeddingProviderMutation();

    // Default action callbacks
    const defaultActions: EmbeddingProviderActions = {
        onViewDetails: (provider) => {
            toast.info(t("dataTables.viewingDetailsFor", { name: provider.display_name }));
            // TODO: Implement view details modal
        },
        onEdit: (provider) => {
            setSelectedProvider(provider);
            setEditDialogOpen(true);
        },
        onToggleEnabled: (provider) => {
            setEnabledEmbeddingProviderMutation.mutate({
                id: provider.id,
                enabled: !provider.enabled,
            });
        },
    };

    const finalActions = actions || defaultActions;
    const columns = createEmbeddingProviderColumns(finalActions);

    const { table } = useDataTable({
        columns,
        data,
        pageCount: 1,
        searchableColumns: EmbeddingProviderSearchableColumns,
    });

    const rows = table.getSelectedRowModel();
    const ids = rows.flatRows.map((row) => row.original.id) as string[];
    const { mutate } = useDeleteEmbeddingProviderMutation();

    const handleDeleteProviders = () => {
        if (confirm(t("dataTables.confirmDeleteEmbeddingProviders"))) {
            ids.forEach((id) => mutate({ id }));
        }
    };

    const handleCreateProvider = () => {
        setCreateDialogOpen(true);
    };

    return (
        <>
            <DataTable
                table={table}
                columns={columns}
                searchableColumns={EmbeddingProviderSearchableColumns}
                newRowButton={
                    <Button variant="outline" size="sm" className="h-8" onClick={handleCreateProvider}>
                        <PlusCircledIcon className="mr-2 size-4" aria-hidden="true" />
                        {t("dataTables.newEmbeddingProvider")}
                    </Button>
                }
                deleteRowsAction={handleDeleteProviders}
            />

            {/* Create Provider Dialog */}
            <EmbeddingProviderDialog open={createDialogOpen} onOpenChange={setCreateDialogOpen} mode="create" />

            {/* Edit Provider Dialog */}
            <EmbeddingProviderDialog
                open={editDialogOpen}
                onOpenChange={setEditDialogOpen}
                provider={selectedProvider}
                mode="edit"
            />
        </>
    );
};

export default EmbeddingProviderDataTable;
